import {memoize} from '@sail/utils';

import type {LocationReturn, LocationSearchObject} from 'src/types';

// Object used to discard all keys that already exist in an empty object
// through Object's prototype, like `__proto__` and others.
const EMPTY = {};
const PATH_PART_PATTERN = /\[.*?\](?=\[|%5B|$)/gi;

function urlToFullLocation(url: string): LocationReturn {
  const parts = url.match(
    /^(?:https?:\/\/[^/]+)?(\/[^?#]*)(?:\?([^#]*))?(?:#(.*)$)?$/,
  );

  if (!parts) {
    throw new TypeError(`Invalid URL: ${url}`);
  }

  return {
    pathname: parts[1],
    search: parts[2] ? stringToObject(parts[2]) : {},
    hash: parts[3] || '',
  };
}

export default memoize(urlToFullLocation);

function stringToObject(string: string): LocationSearchObject {
  const buffer: LocationSearchObject = {};
  const namespaced: {[key: string]: string | string[]} = {};

  string.split('&').forEach((section) => {
    const equal = section.indexOf('=');
    const key = equal === -1 ? section : section.substr(0, equal);
    const value = equal === -1 ? '' : section.substr(equal + 1);
    const decoded = decodeURIComponent(value);

    if (!key && !decoded) {
      return;
    }

    const current = namespaced[key];

    if (current instanceof Array) {
      current.push(decoded);
    } else if (typeof current === 'string') {
      namespaced[key] = [current, decoded];
    } else {
      namespaced[key] = decoded;
    }
  });

  for (const key in namespaced) {
    if (namespaced.hasOwnProperty(key)) {
      const value = namespaced[key];
      const path = keyToPath(key);

      if (value instanceof Array && path[path.length - 1] === '') {
        path.pop();
      }

      let item: LocationSearchObject = buffer;

      for (let i = 0; i < path.length; i++) {
        let key = path[i];

        if (key in EMPTY) {
          break;
        }

        if (key === '') {
          key = item instanceof Array ? `${(item.length || 1) - 1}` : '0';
        }

        if (i === path.length - 1) {
          Object.assign(item, {[key]: value});
        } else if (!item[key]) {
          Object.assign(item, {[key]: /^\d*$/.test(path[i + 1]) ? [] : {}});
        }

        // nosemgrep: prototype-pollution-loop
        item = item[key] as LocationSearchObject;
      }
    }
  }

  return buffer;
}

function keyToPath(key: string): string[] {
  const rawParts = [...key.matchAll(PATH_PART_PATTERN)];

  if (rawParts.length === 0) {
    // The entire path may use encoded delimiters. For example,
    // app%5Brange%5Bfrom%5D%5D becomes ['app', 'range[from]'].
    return decodedKeyToPath(decodeURIComponent(key));
  }

  const path: string[] = [];
  let offset = 0;

  rawParts.forEach((part) => {
    // Encoded delimiters outside raw path parts remain namespace syntax. For
    // example, the gap in app[range]%5Bfrom%5D adds 'from' to the path.
    appendEncodedPath(path, key.slice(offset, part.index));

    // Encoded brackets inside raw delimiters belong to the parameter name. For
    // example, app[range%5Bfrom%5D] adds 'range[from]' to the path.
    path.push(decodeURIComponent(part[0].slice(1, -1)));
    offset = (part.index || 0) + part[0].length;
  });

  appendEncodedPath(path, key.slice(offset));
  return path;
}

function appendEncodedPath(path: string[], encodedPath: string): void {
  if (!encodedPath) {
    return;
  }

  const parts = decodedKeyToPath(decodeURIComponent(encodedPath));
  path.push(...(path.length ? parts.slice(1) : parts));
}

function decodedKeyToPath(key: string): string[] {
  return (key.match(/^[^[\]]*/) || ['']).concat(
    (key.match(PATH_PART_PATTERN) || []).map((part) => part.slice(1, -1)),
  );
}
